import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, CardContent, CardActions, CardMedia,
  Typography, Button, Box, 
  Rating, useTheme, useMediaQuery, Chip, Stack, Tooltip
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { alpha } from '@mui/material/styles';
import { logAnalyticsEvent } from '../../utils/analytics';
import { getProductUrl } from '../../utils/productUtils';
import { ShoppingCartIcon } from '../../icons';
import { color, r } from '../../theme/tokens';

/**
 * Flexible ProductCard component that can be used in both explorer and stack views
 * @param {Object} product - The product data to display
 * @param {Function} onAddToStack - Optional callback for adding to stack
 * @param {boolean} compact - Whether to use compact styling
 * @param {string} layout - "vertical" (default/explorer) or "horizontal" (stack view)
 * @param {boolean} linkToProductPage - Whether to link to product detail page (vertical only)
 */
const ProductCard = ({ 
  product, 
  onAddToStack, 
  compact = false,
  layout = 'vertical', // 'vertical' for explorer, 'horizontal' for stack
  linkToProductPage = true, // Only applies to vertical layout
  onBeforeNavigate, // Optional callback before navigating (for scroll restoration)
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isHorizontal = layout === 'horizontal';
  
  // Unique ID for scroll restoration
  const cardId = `product-card-${product?.id || product?.asin}`;
  
  // Generate product page URL
  const productUrl = getProductUrl(product);
  
  // Handle card click (vertical layout only)
  const handleCardClick = (e) => {
    // Don't navigate if horizontal layout or if clicking on a button/link
    if (isHorizontal) return;
    if (e.target.closest('button') || e.target.closest('a')) return;
    
    if (linkToProductPage && productUrl) {
      // Call onBeforeNavigate for scroll restoration
      if (onBeforeNavigate) {
        onBeforeNavigate(product?.id || product?.asin);
      }
      
      logAnalyticsEvent('select_item', {
        item_id: product?.asin || product?.id,
        item_name: product?.product_name,
        item_brand: product?.brand,
        item_category: 'product',
      });
      navigate(productUrl);
    }
  };
  
  // Extract and format product data with fallbacks
  const displayTitle = product?.product_name || product?.title || '';
  
  // Format helper function
  const formatString = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1) : null;
  const formattedFormFactor = formatString(product?.form_factor);
  const formattedNutrientForm = formatString(product?.specific_nutrient_form);
  
  // Extract and deduplicate certifications
  const uniqueCertifications = React.useMemo(() => {
    const certifications = [
      ...(product?.usda_organic ? ['USDA Organic'] : []),
      ...(Array.isArray(product?.testing_3rd_party) ? product.testing_3rd_party : []),
      ...(Array.isArray(product?.other_certifications) ? product.other_certifications : []),
      ...(Array.isArray(product?.dietary_attributes) ? product.dietary_attributes : [])
    ].filter(Boolean);
    
    // Deduplicate based on lowercase comparison
    return [...new Set(
      certifications.map(cert => typeof cert === 'string' ? cert.toLowerCase() : '')
    )].map(lowerCert => {
      if (!lowerCert) return null;
      return certifications.find(cert => 
        typeof cert === 'string' && cert.toLowerCase() === lowerCert
      ) || lowerCert;
    }).filter(Boolean);
  }, [
    product?.usda_organic,
    product?.testing_3rd_party,
    product?.other_certifications, 
    product?.dietary_attributes
  ]);
  
  // Check if price exists and is valid
  const hasPrice = product?.price && !isNaN(parseFloat(product.price));
  const hasRating = product?.rating && product.rating > 0;
  
  // Format price
  const formatPrice = (price) => {
    if (!price) return '$0.00';
    return `$${parseFloat(price).toFixed(2)}`;
  };
  
  // Get proper unit text based on form factor and count
  const getUnitText = (formFactor, count) => {
    const quantity = parseInt(count) || 1;
    
    if (!formFactor) return quantity === 1 ? 'unit' : 'units';
    
    const lowerForm = formFactor.toLowerCase();
    
    // Handle countable items (capsules, tablets, gummies)
    if (lowerForm.includes('capsule') || lowerForm.includes('tablet') || 
        lowerForm.includes('gummy') || lowerForm.includes('pill') || 
        lowerForm.includes('softgel') || lowerForm.includes('gelcap')) {
      return quantity === 1 
        ? lowerForm.replace(/s$/, '') // Remove trailing 's' if present 
        : lowerForm;
    }
    
    // Handle non-countable - powder
    if (lowerForm.includes('powder')) {
      return quantity === 1 ? 'scoop of powder' : 'scoops of powder';
    }
    
    // Handle non-countable - liquid
    if (lowerForm.includes('liquid') || lowerForm.includes('oil') || 
        lowerForm.includes('tincture') || lowerForm.includes('solution')) {
      return 'ml';
    }
    
    return formFactor;
  };
  
  // Create serving information display
  const servingDisplay = React.useMemo(() => {
    const display = [];
    
    // Add serving size if available
    if (product?.serving_size) {
      const servingSize = parseInt(product.serving_size) || 0;
      const unit = getUnitText(product.form_factor, product.serving_size);
        
      display.push({
        text: `${product.serving_size} ${unit}/serving`,
        value: servingSize,
        type: 'serving_size'
      });
    }
    
    // Add dosage per serving if available
    if (product?.dosage_amount) {
      const dosageAmount = parseFloat(product.dosage_amount) || 0;
      display.push({
        text: `${product.dosage_amount} ${product.dosage_unit || ''}/serving`,
        value: dosageAmount,
        type: 'dosage'
      });
    }
    
    // Add servings per bottle if available
    if (product?.servings_per_bottle) {
      const servingsCount = parseFloat(product.servings_per_bottle) || 0;
      display.push({
        text: `${product.servings_per_bottle} servings total`,
        value: servingsCount,
        type: 'servings'
      });
    }

    // Sort serving information by type priority
    return display.sort((a, b) => {
      const typeOrder = { 'serving_size': 1, 'dosage': 2, 'servings': 3 };
      return typeOrder[a.type] - typeOrder[b.type];
    });
  }, [
    product?.serving_size,
    product?.dosage_amount,
    product?.dosage_unit,
    product?.servings_per_bottle,
    product?.form_factor
  ]);
  
  // Render certification chips with a fixed limit and tooltips for overflow
  const renderCertificationChips = (maxToShow = 3) => {
    if (uniqueCertifications.length === 0) return null;
    
    const visibleCount = uniqueCertifications.length > maxToShow ? maxToShow - 1 : uniqueCertifications.length;
    const hasMore = uniqueCertifications.length > maxToShow;
    const hiddenCerts = hasMore ? uniqueCertifications.slice(visibleCount) : [];
  
    return (
      <Box sx={{ 
        display: 'flex', 
        gap: 0.4, 
        flexWrap: 'wrap',
        py: 0.2 
      }}>
        {uniqueCertifications.slice(0, visibleCount).map((cert) => (
          <Tooltip 
            key={cert} 
            title={cert}
            placement="top"
            arrow
          >
            <Chip 
              label={cert} 
              size="small" 
              sx={{ 
                height: { xs: '1rem', sm: '1.125rem' },
                fontSize: { xs: '0.55rem', sm: '0.6rem' },
                flexShrink: 0,
                backgroundColor: 'rgba(240, 240, 240, 0.9)',
                border: 'none',
                color: 'rgba(100, 100, 100, 0.95)',
                fontWeight: 500,
                '& .MuiChip-label': { 
                  px: 0.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: { xs: '5rem', sm: '6.25rem' }
                }
              }}
            />
          </Tooltip>
        ))}
        {hasMore && (
          <Tooltip 
            title={
              <React.Fragment>
                <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 0.5, display: 'block' }}>
                  Additional certifications:
                </Typography>
                {hiddenCerts.map((cert, index) => (
                  <Typography key={index} variant="caption" display="block" sx={{ pl: 1 }}>
                    • {cert}
                  </Typography>
                ))}
              </React.Fragment>
            }
            placement="top"
            arrow
          >
            <Chip 
              label={`+${uniqueCertifications.length - visibleCount}`} 
              size="small" 
              sx={{ 
                height: { xs: '1rem', sm: '1.125rem' },
                fontSize: { xs: '0.55rem', sm: '0.6rem' },
                flexShrink: 0,
                backgroundColor: 'rgba(240, 240, 240, 0.9)',
                border: 'none',
                color: 'rgba(100, 100, 100, 0.95)',
                fontWeight: 500,
                '& .MuiChip-label': { px: 0.5 }
              }}
            />
          </Tooltip>
        )}
      </Box>
    );
  };
  
  // Horizontal layout (stack view)
  if (isHorizontal) {
    return (
      <Card 
        sx={{ 
          display: 'flex',
          height: '100%',
          maxHeight: { xs: '8.125rem', sm: '8.125rem', md: '8.75rem' },
          minHeight: { xs: '8.125rem', sm: '8.125rem', md: '8.75rem' },
          borderRadius: '0.5rem',
          overflow: 'hidden',
          boxShadow: '0 0.125rem 0.5rem rgba(0,0,0,0.05)',
          position: 'relative',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            transform: 'translateY(-0.25rem)',
            boxShadow: '0 0.375rem 0.75rem rgba(0,0,0,0.1)'
          }
        }}
      >
        {/* Product Image */}
        <CardMedia
          component="img"
          sx={{
            // rem, paired with the `calc(100% - …)` on the content column
            // below: the two must scale together or they part company when
            // the OS text size grows.
            width: { xs: r(80), sm: r(90), md: r(100) },
            height: '100%',
            objectFit: 'contain',
            backgroundColor: color.surface,
            p: { xs: 1, sm: 0.8, md: 0.8 }
          }}
          image={product?.image_url || product?.imageUrl}
          alt={displayTitle}
        />
        
        {/* Content */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          width: { xs: `calc(100% - ${r(80)})`, sm: `calc(100% - ${r(90)})`, md: `calc(100% - ${r(100)})` },
          overflow: 'hidden',
          position: 'relative'
        }}>
          <CardContent sx={{ 
            padding: { xs: 1, sm: 1, md: 1 },
            paddingBottom: '0 !important',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            '&:last-child': { pb: 0 }
          }}>
            {/* 1. Title area - with proper truncation and tooltip */}
            <Tooltip 
              title={displayTitle}
              placement="top"
              arrow
              enterTouchDelay={0}
              leaveTouchDelay={3000}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' },
                  lineHeight: 1.3,
                  letterSpacing: '0.01em',
                  mb: 0.3,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  color: 'text.primary',
                  cursor: 'pointer'
                }}
              >
                {displayTitle}
              </Typography>
            </Tooltip>
            
            {/* 2. Middle area - for form factor and serving info */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              gap: 0.4,
              mb: { xs: 0.3, sm: 0.5, md: 0.6 }
            }}>
              {/* Form factors in first row */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                {/* Form factor */}
                {formattedFormFactor && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontSize: { xs: '0.65rem', sm: '0.7rem' },
                      padding: '0.0625rem 0.3125rem',
                      borderRadius: '0.1875rem',
                      backgroundColor: 'rgba(245, 245, 245, 0.9)',
                      border: '0.0625rem solid rgba(230, 230, 230, 0.9)',
                      fontWeight: 500,
                      color: 'rgba(80, 80, 80, 0.95)'
                    }}
                  >
                    {formattedFormFactor}
                  </Typography>
                )}
                
                {/* Supplement Nutrient Form */}
                {formattedNutrientForm && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontSize: { xs: '0.65rem', sm: '0.7rem' },
                      padding: '0.0625rem 0.3125rem',
                      borderRadius: '0.1875rem',
                      backgroundColor: 'rgba(240, 247, 255, 0.9)',
                      border: '0.0625rem solid rgba(225, 237, 250, 0.9)',
                      fontWeight: 500,
                      color: 'rgba(70, 100, 145, 0.95)'
                    }}
                  >
                    {formattedNutrientForm}
                  </Typography>
                )}
              </Box>
                  
              {/* Serving info in second row */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                {/* Display serving size info */}
                {product?.serving_size && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontSize: { xs: '0.65rem', sm: '0.7rem' },
                      padding: '0rem 0.25rem',
                      borderRadius: '0.1875rem',
                      backgroundColor: 'rgba(250, 250, 250, 0.9)',
                      border: '0.0625rem solid rgba(240, 240, 240, 0.9)',
                      color: 'rgba(90, 90, 90, 0.9)',
                    }}
                  >
                    <strong>Take:</strong> {product.serving_size} {getUnitText(product.form_factor, product.serving_size)}/serving
                  </Typography>
                )}
                
                {/* Combined dosage and total servings - more compact */}
                {(product?.dosage_amount || product?.servings_per_bottle) && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontSize: { xs: '0.65rem', sm: '0.7rem' },
                      padding: '0rem 0.25rem',
                      borderRadius: '0.1875rem',
                      backgroundColor: 'rgba(250, 250, 250, 0.9)',
                      border: '0.0625rem solid rgba(240, 240, 240, 0.9)',
                      color: 'rgba(90, 90, 90, 0.9)',
                    }}
                  >
                    {product?.dosage_amount ? <><strong>Dosage:</strong> {product.dosage_amount}{product.dosage_unit || ''}</> : ''}
                    {product?.dosage_amount && product?.servings_per_bottle ? ' • ' : ''}
                    {product?.servings_per_bottle ? <><strong>Total:</strong> {product.servings_per_bottle} servings</> : ''}
                  </Typography>
                )}
              </Box>
            </Box>
              
            {/* 3. Certifications area */}
            {uniqueCertifications.length > 0 && (
              <Box 
                sx={{ 
                  display: 'flex',
                  overflowX: 'auto',
                  mb: 0.5,
                  mt: { xs: 0.2, sm: 0.4, md: 0.6 },
                  maxWidth: '100%',
                  minHeight: { xs: '1rem', sm: '1.25rem' },
                  '&::-webkit-scrollbar': {
                    height: '0.125rem',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(0,0,0,0.1)',
                    borderRadius: '0.125rem',
                  }
                }}
              >
                {renderCertificationChips(3)}
              </Box>
            )}
            
            {/* 4. Bottom section with action button */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'flex-end',
              alignItems: 'center',
              mt: 'auto',
              pt: { xs: 0.1, sm: 0.2 },
              position: 'relative',
              width: '100%'
            }}>
              <Button
                variant="contained"
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.7rem', md: '0.75rem' },
                  py: { xs: 0.3, sm: 0.4 },
                  px: { xs: 1.2, sm: 1.5 },
                  minWidth: { xs: '3.4375rem', sm: '3.75rem' },
                  height: { xs: '1.625rem', sm: '1.75rem', md: '1.875rem' },
                  borderRadius: '0.25rem',
                  textTransform: 'none',
                  fontWeight: 600,
                  position: 'absolute',
                  right: { xs: '0.5rem', sm: '0.625rem', md: '0.75rem' },
                  bottom: { xs: 0, sm: 0 },
                  zIndex: 10,
                  boxShadow: '0 0.125rem 0.25rem rgba(0,0,0,0.2)',
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                    boxShadow: '0 0.1875rem 0.375rem rgba(0,0,0,0.25)'
                  }
                }}
                href={product?.affiliate_link || product?.affiliateLink}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                onClick={() => {
                  logAnalyticsEvent('click_buy_product', {
                    item_id: product?.asin || product?.id || 'unknown',
                    item_name: displayTitle,
                    item_category: 'product',
                    item_brand: product?.brand || 'unknown',
                    supplement_id: product?.supplementKey || 'unknown',
                    item_list_context: 'product_explorer' // Assume explorer context
                  });
                }}
              >
                Buy
              </Button>
            </Box>
          </CardContent>
        </Box>
      </Card>
    );
  }
  
  // Vertical layout (explorer view)
  return (
    <Card 
      id={cardId}
      onClick={handleCardClick}
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        borderRadius: '0.5rem',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        overflow: 'hidden',
        cursor: linkToProductPage ? 'pointer' : 'default',
        '&:hover': {
          transform: `translateY(-${r(4)})`,
          boxShadow: `0 ${r(8)} ${r(16)} rgba(0,0,0,0.1)`,
        },
      }}
    >
      {/* Image container with hover overlay */}
      <Box sx={{ position: 'relative', overflow: 'hidden' }}>
        <CardMedia
          component="img"
          image={product?.image_url || product?.imageUrl}
          alt={displayTitle}
          sx={{
            height: compact ? { xs: 140, md: 160 } : { xs: 180, md: 200 },
            objectFit: 'contain',
            p: { xs: 2, md: 1.5 },
            backgroundColor: theme.palette.grey[50],
            transition: 'transform 0.3s ease',
          }}
        />
      </Box>
      
      <CardContent sx={{ 
        flexGrow: 1, 
        p: compact ? { xs: 1.2, md: 1.5 } : { xs: 1.5, md: 1.8 }, 
        pb: { xs: 0.5, md: 0.8 },
        '&:last-child': { pb: { xs: 0.5, md: 0.8 } },
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Product Title with tooltip */}
        <Tooltip 
          title={displayTitle}
          placement="top"
          arrow
          enterTouchDelay={0}
          leaveTouchDelay={3000}
        >
          <Typography 
            variant="body2" 
            color="text.primary"
            sx={{ 
              fontSize: compact ? { xs: '0.8rem', md: '0.9rem' } : { xs: '0.85rem', md: '0.95rem' },
              fontWeight: 500,
              letterSpacing: '0.01em',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              lineHeight: 1.3,
              mb: 0.8,
              cursor: 'pointer'
            }}
          >
            {/* Product title display */}
            {displayTitle}
          </Typography>
        </Tooltip>
        
        {/* Form Factor and Nutrient Form Section */}
        <Box sx={{ mb: 0.8, display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
          {/* Form Factor */}
          {formattedFormFactor && (
            <Typography
              variant="body2"
              sx={{
                display: 'inline-block',
                fontSize: '0.75rem',
                fontWeight: 500,
                padding: '0.0625rem 0.3125rem',
                borderRadius: '0.1875rem',
                backgroundColor: 'rgba(245, 245, 245, 0.9)',
                border: '0.0625rem solid rgba(230, 230, 230, 0.9)',
                color: 'rgba(80, 80, 80, 0.95)'
              }}
            >
              {formattedFormFactor}
            </Typography>
          )}
          
          {/* Nutrient Form */}
          {formattedNutrientForm && (
            <Typography
              variant="body2"
              sx={{
                display: 'inline-block',
                fontSize: '0.75rem',
                fontWeight: 500,
                padding: '0.0625rem 0.3125rem',
                borderRadius: '0.1875rem',
                backgroundColor: 'rgba(240, 247, 255, 0.9)',
                border: '0.0625rem solid rgba(225, 237, 250, 0.9)',
                color: 'rgba(70, 100, 145, 0.95)'
              }}
            >
              {formattedNutrientForm}
            </Typography>
          )}
        </Box>
        
        {/* Certifications and Tags with tooltips */}
        {uniqueCertifications.length > 0 && (
          <Box 
            sx={{ 
              display: 'flex',
              mb: 0.8,
              minHeight: '1.25rem'
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              gap: 0.5, 
              flexWrap: 'wrap',
              py: 0.2
            }}>
              {uniqueCertifications.length <= 3 ? (
                // Show all if 3 or fewer
                uniqueCertifications.map((cert) => (
                  <Tooltip 
                    key={cert} 
                    title={cert}
                    placement="top"
                    arrow
                  >
                    <Chip
                      label={cert}
                      size="small"
                      sx={{ 
                        height: '1.25rem',
                        fontSize: '0.65rem',
                        borderRadius: '0.25rem',
                        backgroundColor: 'rgba(240, 240, 240, 0.9)',
                        border: 'none',
                        color: 'rgba(100, 100, 100, 0.95)',
                        fontWeight: 500,
                        flexShrink: 0,
                        '& .MuiChip-label': { 
                          px: 0.8,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: r(120)
                        }
                      }}
                    />
                  </Tooltip>
                ))
              ) : (
                // Show first 2 plus "+X more" chip with tooltip
                <>
                  {uniqueCertifications.slice(0, 2).map((cert) => (
                    <Tooltip 
                      key={cert} 
                      title={cert}
                      placement="top"
                      arrow
                    >
                      <Chip
                        label={cert}
                        size="small"
                        sx={{ 
                          height: '1.25rem',
                          fontSize: '0.65rem',
                          borderRadius: '0.25rem',
                          backgroundColor: 'rgba(240, 240, 240, 0.9)',
                          border: 'none',
                          color: 'rgba(100, 100, 100, 0.95)',
                          fontWeight: 500,
                          flexShrink: 0,
                          '& .MuiChip-label': { 
                            px: 0.8,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: r(120)
                          }
                        }}
                      />
                    </Tooltip>
                  ))}
                  <Tooltip 
                    title={
                      <React.Fragment>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 0.5, display: 'block' }}>
                          Additional certifications:
                        </Typography>
                        {uniqueCertifications.slice(2).map((cert, index) => (
                          <Typography key={index} variant="caption" display="block" sx={{ pl: 1 }}>
                            • {cert}
                          </Typography>
                        ))}
                      </React.Fragment>
                    }
                    placement="top"
                    arrow
                    enterTouchDelay={0}
                    leaveTouchDelay={3000}
                  >
                    <Chip 
                      label={`+${uniqueCertifications.length - 2}`}
                      size="small" 
                      sx={{ 
                        height: '1.25rem',
                        fontSize: '0.65rem',
                        borderRadius: '0.25rem',
                        backgroundColor: 'rgba(240, 240, 240, 0.9)',
                        border: 'none',
                        color: 'rgba(100, 100, 100, 0.95)',
                        fontWeight: 500,
                        flexShrink: 0,
                        '& .MuiChip-label': { px: 0.8 }
                      }}
                    />
                  </Tooltip>
                </>
              )}
            </Box>
          </Box>
        )}
        
        {/* Product Details - Evenly spaced list */}
        <Stack spacing={0.2} sx={{ mb: 1, mt: 'auto' }}>
          {product?.serving_size && (
            <Typography sx={{ fontSize: '0.8rem', color: 'text.primary', lineHeight: 1.3 }}>
              <strong>Serving Size:</strong> {product.serving_size} {getUnitText(product.form_factor, product.serving_size)}
            </Typography>
          )}
        
          {/* Combine dosage and total servings on the same line when both are available */}
          {(product?.dosage_amount || product?.servings_per_bottle) && (
            <Typography sx={{ fontSize: '0.8rem', color: 'text.primary', lineHeight: 1.3, display: 'flex', flexWrap: 'wrap', gap: r(8) }}>
              {product?.dosage_amount && (
                <span><strong>Per Serving:</strong> {product.dosage_amount} {product.dosage_unit || ''}</span>
              )}
              {product?.dosage_amount && product?.servings_per_bottle && (
                <span style={{ color: 'rgba(0, 0, 0, 0.3)' }}>•</span>
              )}
              {product?.servings_per_bottle && (
                <span><strong>Total:</strong> {product.servings_per_bottle} servings</span>
              )}
            </Typography>
          )}
        </Stack>
      </CardContent>
      
      <CardActions sx={{ 
        p: compact ? 1.2 : 1.5, 
        pt: 0, 
        mt: 0,
        position: 'relative',
        flexDirection: 'column',
        gap: 0.75,
      }}>
        {/* Primary: Buy on Amazon (fast path to purchase) */}
        <Button 
          variant="contained"
          fullWidth
          href={product?.affiliate_link || product?.affiliateLink}
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<ShoppingCartIcon fontSize="small" />}
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            logAnalyticsEvent('click_buy_product', {
              item_id: product?.asin || product?.id || 'unknown',
              item_name: displayTitle,
              item_category: 'product',
              item_brand: product?.brand || 'unknown',
              supplement_id: product?.supplementKey || 'unknown',
              item_list_context: 'product_card'
            });
          }}
          sx={(theme) => ({
            textTransform: 'none',
            borderRadius: '0.375rem',
            py: 0.6,
            zIndex: 10,
            fontWeight: 600,
            // Deep evergreen with white text — the one sanctioned exception to
            // the brief's one-action-colour rule (§6), which the Signal Accent
            // still owns everywhere else in the product. Ruled for the
            // affiliate Buy CTA by Tanu on 2026-08-11, STACK-510; recorded in
            // docs/design-language/DESIGN_TOKENS.md so it is not restored as a
            // bug. The brief's "never white on a fill" prohibition is scoped to
            // the accent — white on `evergreen` is 9.60:1, a ratio the brief
            // itself records. The `!important` on the label and the start icon
            // is carried over from the dark-ink version (STACK-503), where it
            // was load-bearing against MUI's contained-primary default. White
            // happens to match that default now, so keeping it is belt and
            // braces — but dropping it would leave this button's colour
            // depending on a default that is not this component's to hold.
            background: color.evergreen,
            color: `${theme.palette.primary.contrastText} !important`,
            boxShadow: `0 0.125rem 0.25rem ${alpha(color.evergreen, 0.18)}`,
            '& .MuiButton-startIcon, & .MuiButton-startIcon svg, & .MuiButton-startIcon path': {
              color: `${theme.palette.primary.contrastText} !important`,
              fill: 'currentColor',
            },
            '&:hover': {
              background: color.evergreenHover,
              color: `${theme.palette.primary.contrastText} !important`,
              boxShadow: `0 0.1875rem 0.5rem ${alpha(color.evergreen, 0.18)}`,
            }
          })}
        >
          Buy on Amazon
        </Button>
        
        {/* Secondary: View Details link (for SEO, links to product page) */}
        {linkToProductPage && (
          <Button 
            variant="text"
            fullWidth
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              logAnalyticsEvent('select_item', {
                item_id: product?.asin || product?.id || 'unknown',
                item_name: displayTitle,
                item_category: 'product',
                item_brand: product?.brand || 'unknown',
                supplement_id: product?.supplementKey || 'unknown',
                item_list_context: 'product_explorer'
              });
              navigate(productUrl);
            }}
            sx={{ 
              textTransform: 'none',
              py: 0.3,
              fontSize: '0.75rem',
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.04)',
                color: 'primary.main',
              }
            }}
          >
            View Details
          </Button>
        )}
      </CardActions>
    </Card>
  );
};

export default ProductCard; 